import React, { useState } from "react";
import { toast } from "sonner";
import { Flag } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const REASONS = [
  { value: "not_a_card", label: "Not a trading card" },
  { value: "inappropriate", label: "Inappropriate image" },
  { value: "wrong_info", label: "Wrong card info" },
  { value: "other", label: "Something else" },
];

export default function ReportDialog({ cardId }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("not_a_card");
  const [details, setDetails] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      await api.post("/reports", { card_id: cardId, reason, details });
      toast.success("Report sent — thanks for keeping CardArk clean");
      setOpen(false);
      setDetails("");
    } catch (e) {
      toast.error(e.response?.status === 401 ? "Log in to report" : "Couldn't send the report");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid={`report-button-${cardId}`}
        title="Report this image"
        onClick={() => setOpen(true)}
        className="w-7 h-7 rounded-full flex items-center justify-center bg-zinc-900 border border-white/25 text-zinc-300 shadow-lg hover:border-red-400/60 hover:text-red-400 transition-colors"
      >
        <Flag className="w-3 h-3" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="report-dialog" className="bg-[#111113] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Report this upload</DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">
              Flag images that aren't trading cards or don't belong here. Reports go to the CardArk team.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {REASONS.map((r) => (
                <button
                  key={r.value}
                  data-testid={`report-reason-${r.value}`}
                  onClick={() => setReason(r.value)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${reason === r.value ? "border-red-400/60 text-red-300 bg-red-500/10" : "border-white/15 text-zinc-400 hover:text-white"}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <Textarea
              data-testid="report-details-input"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Anything else we should know? (optional)"
              maxLength={500}
              className="bg-black/30 border-white/10 text-sm min-h-[70px]"
            />
            <Button
              data-testid="report-send-button"
              onClick={send}
              disabled={sending}
              variant="outline"
              className="border-red-400/40 text-red-300 hover:bg-red-500/10 rounded-full w-full"
            >
              {sending ? "Sending..." : "Send report"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
